import { debounce } from 'lodash-es'
import { useCallback, useContext, useEffect } from 'react'
import { LuCrosshair, LuLocate } from 'react-icons/lu'

import { MapMarker } from '@/feature/map'
import { MapContext } from '@/feature/map/context/map'
import { ObservatoryContext, useObservatoryPageStore } from '@/feature/observatory'
import { usePersistentObservatoryPageStore } from '@/feature/observatory/store/usePersistentObservatoryPageStore'

import { useReverseGeocodedPosition } from '../../../hooks'
import styles from '../../GeoFenceCreateEdit.module.css'
import { GeoFenceTargetProps } from '../../GeoFenceCreateEdit.types'

export const GeoFenceTarget = ({
  onMove,
  children,
  isDraggable = true,
  ...props
}: GeoFenceTargetProps) => {
  const { unlockMap } = useContext(ObservatoryContext)

  const searchLocation = usePersistentObservatoryPageStore((state) => state.searchLocation)

  const map = useObservatoryPageStore((state) => state.map)

  const updateFromPosition = useReverseGeocodedPosition()

  const onMarkerDragged = useCallback(
    debounce((e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        updateFromPosition(e.latLng)
      }
    }, 300),
    [],
  )

  useEffect(() => {
    unlockMap()

    // Read the current store value directly to avoid stale closure issues.
    // usePanGeoFenceIntoView sets searchLocation in useLayoutEffect (which runs before
    // any useEffect), but the closure would capture the old render value — getState()
    // gives us the up-to-date value after that layout effect has run.
    const currentSearchLocation = usePersistentObservatoryPageStore.getState().searchLocation
    if (!currentSearchLocation?.location) {
      const pos = map?.map?.getCenter()
      if (pos) {
        updateFromPosition(pos)
      }
    }
  }, [])

  const markerEventProps = isDraggable
    ? {
        onDrag: (e: google.maps.MapMouseEvent) => {
          onMove(e.latLng)
          onMarkerDragged(e)
        },
        onDragEnd: (e: google.maps.MapMouseEvent) => {
          onMove(e.latLng)
          onMarkerDragged(e)
        },
      }
    : {}

  const hasValidPosition =
    searchLocation?.location &&
    Number.isFinite(searchLocation.location.lat) &&
    Number.isFinite(searchLocation.location.lng)

  return hasValidPosition && map ? (
    <MapContext.Provider value={map}>
      <MapMarker
        position={searchLocation.location}
        shadowRootCss={'.IPAZAH-content-container ~ * { display: none; }'}
        className={styles.searchMarker}
        animate={false}
        clusterer={false}
        zIndex={10000}
        {...markerEventProps}
        {...props}
      >
        <div className={styles.searchTarget} data-testid="geofence-target">
          {children || (
            <>
              <LuCrosshair size={40} strokeWidth={1.5} className={styles.hover} />
              <LuLocate size={40} strokeWidth={1.6} className={styles.nohover} />
            </>
          )}
        </div>
      </MapMarker>
    </MapContext.Provider>
  ) : null
}
